import { defaultMermaidContent } from './mermaid-types';
import { blankBeatMeta, slideIdFor } from './routing';
import type {
  Annotation,
  AnnotationKind,
  Column,
  ElementPosition,
  ElementType,
  RevealStep,
  Row,
  RowLayout,
  ScriptElement,
  Slide,
  SlideKind,
} from './schema.types';
import { uid } from './utils';

export function createColumn(widthPercent = 100): Column {
  return { id: uid('c'), widthPercent, elements: [] };
}

export function createRow(layout: RowLayout = 1): Row {
  const columnCount = layout === 0 ? 1 : layout;
  const widthPercent = Math.floor(100 / columnCount);
  const columns: Column[] = Array.from({ length: columnCount }, () => createColumn(widthPercent));
  return {
    id: uid('r'),
    layout,
    // Every column is a freeform canvas now, so give new rows a comfortable
    // default height to drop and arrange blocks in.
    heightPercent: 40,
    padding: { top: 16, right: 32, bottom: 16, left: 32 },
    columns,
  };
}

/** Short prefix per slide kind, used to mint beat ids like `t8x2k4`. */
const KIND_PREFIX: Record<SlideKind, string> = {
  teach: 't',
  quiz: 'q',
  summary: 's',
  pace_check: 'p',
  confidence_check: 'c',
  engagement_reset: 'e',
};

/** Mint a fresh beat id (e.g. `t8x2k4`). Optional `existing` set lets the
 *  caller avoid collisions inside the current pack — extremely unlikely with
 *  6+ chars of randomness, but cheap insurance. */
export function mintBeatId(kind: SlideKind, existing?: Set<string>): string {
  for (let i = 0; i < 5; i++) {
    const id = `${KIND_PREFIX[kind]}${Math.random().toString(36).slice(2, 8)}`;
    if (!existing || !existing.has(id)) return id;
  }
  // Vanishingly unlikely; append entropy and ship it.
  return `${KIND_PREFIX[kind]}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Create a slide.
 *
 * - **Script mode (default — no opts):** returns a script-shaped slide with
 *   the historical `uid('slide')` id (`slide-<token>`) and **no** `beatMeta`.
 *   Existing call sites (LeftToolbar "+ NEW SLIDE", SuggestSlideDialog,
 *   duplicate) continue to work unchanged.
 * - **Pack mode (opts.kind set):** returns a pack slide with a freshly minted
 *   beat id (`t...` / `q...` / etc), `slide-${beatId}` id, and a full
 *   `beatMeta` scaffold per SCHEMA_USAGE.md Rule 1. The store's `insertSlide`
 *   then rewires `beatMeta.next.default` to the routing successor.
 */
export function createSlide(opts?: {
  label?: string;
  kind?: SlideKind;
  existingBeatIds?: Set<string>;
}): Slide {
  const label = opts?.label ?? 'Untitled slide';

  if (!opts?.kind) {
    return {
      id: uid('slide'),
      label,
      background: '#ffffff',
      transition: 'fade',
      revealSteps: [],
      rows: [createRow(1)],
    };
  }

  const beatId = mintBeatId(opts.kind, opts.existingBeatIds);
  return {
    id: slideIdFor(beatId),
    label,
    background: '#ffffff',
    transition: 'fade',
    revealSteps: [],
    rows: [createRow(1)],
    beatMeta: blankBeatMeta(beatId, opts.kind),
  };
}

const defaultStyle: Record<ElementType, ScriptElement['style']> = {
  text: { fontSize: 18, fontWeight: 400, color: '#1f2937', textAlign: 'left' },
  image: { borderRadius: 8 },
  shape: { backgroundColor: '#6366f1', borderRadius: 8 },
  mcq: { fontSize: 16, color: '#111827' },
  chip: { fontSize: 13, color: '#1f2937', backgroundColor: '#e0e7ff', borderRadius: 999 },
  video: { borderRadius: 8 },
  embed: { borderRadius: 8 },
  '3d': { borderRadius: 8 },
  latex: { fontSize: 18, color: '#1f2937' },
  icon: { color: '#6366f1' },
  callout: { fontSize: 14, color: '#1f2937' },
  mermaid: {},
  timeline: { fontSize: 16, color: '#1f2937' },
};

const defaultContent: Record<ElementType, ScriptElement['content']> = {
  text: 'Tap to edit text',
  image: { src: 'https://placehold.co/600x400?text=Image', alt: 'placeholder' },
  shape: { shape: 'rect', fill: '#6366f1' },
  mcq: {
    question: 'Sample question?',
    options: ['Option A', 'Option B', 'Option C', 'Option D'],
    answerIndex: 0,
  },
  chip: { label: 'Chip' },
  video: { src: '' },
  embed: { url: 'https://example.com', height: 320 },
  '3d': { src: '' },
  latex: { source: 'E = mc^2', displayMode: true },
  icon: { id: 'idea', size: 64 },
  callout: { variant: 'important_concept', body: 'Tap to edit this callout.' },
  mermaid: defaultMermaidContent(),
  timeline: {
    items: [
      { id: uid('tl'), label: '1665', body: 'First event…' },
      { id: uid('tl'), label: '1670s', body: 'Second event…' },
    ],
  },
};

/**
 * Pack-only: build a fully-formed quiz slide per SCHEMA_USAGE.md Rule 4
 * Pattern A. Exactly ONE mcq element, with optionIds + correctOptionId
 * mirrored on both the element and beatMeta (the editor + Electron must
 * agree). Thresholds + an empty branches map for every wrong option are
 * pre-populated so the slide is structurally valid the moment it's inserted.
 *
 * Default shape: 4 options labeled "Option A/B/C/D", correct = "a", easy
 * difficulty, 70%/40% advance/reteach thresholds. The teacher edits the
 * question text + correct answer afterwards.
 *
 * Use [[insertSlide]] / [[addPackQuizSlide store action]] to actually splice
 * into the routing graph — this factory only produces the slide object.
 */
export function createQuizSlide(opts?: {
  label?: string;
  existingBeatIds?: Set<string>;
}): Slide {
  const beatId = mintBeatId('quiz', opts?.existingBeatIds);
  const slideId = slideIdFor(beatId);
  const label = opts?.label ?? 'Quiz check';

  // Build the mcq element. optionIds map 1:1 to options[]; correctOptionId
  // matches the first option by default.
  const mcqId = uid('el');
  const options = ['Option A', 'Option B', 'Option C', 'Option D'];
  const optionIds = ['a', 'b', 'c', 'd'];
  const correctOptionId = optionIds[0];

  const mcq: ScriptElement = {
    id: mcqId,
    type: 'mcq',
    content: {
      question: 'New question — tap to edit',
      options,
      answerIndex: 0,
      optionIds,
      correctOptionId,
    },
    style: { fontSize: 16, color: '#111827' },
  };

  // One column with the mcq element, plus a single reveal step.
  const column: Column = { id: uid('c'), widthPercent: 100, elements: [mcq] };
  const row: Row = {
    id: uid('r'),
    layout: 1,
    heightPercent: 100,
    padding: { top: 24, right: 32, bottom: 24, left: 32 },
    columns: [column],
  };

  const step: RevealStep = {
    id: uid('step'),
    label: 'Question',
    elementIds: [mcqId],
    elementAnimations: { [mcqId]: { animation: 'fade', durationMs: 400, delayMs: 0 } },
    audio: {
      text: 'Read the question and pick the right answer.',
      source: 'tts',
    },
  };

  // beatMeta: blank scaffold + quiz-specific fields. branches[] gets one
  // entry per WRONG option, keyed by optionId, so Electron's branch lookup
  // never misses (per Quiz consistency rule 5).
  const baseMeta = blankBeatMeta(beatId, 'quiz');
  const branches: Record<string, Record<string, unknown>> = {};
  for (const id of optionIds) {
    if (id === correctOptionId) continue;
    branches[id] = {
      misconceptionId: null,
      diagnosis: '',
      reteachLevel1: [],
      reteachLevel2: [],
    };
  }

  return {
    id: slideId,
    label,
    background: '#ffffff',
    transition: 'fade',
    rows: [row],
    revealSteps: [step],
    beatMeta: {
      ...baseMeta,
      cognitiveLoad: 'medium',
      difficulty: 'easy',
      correctOptionId,
      thresholds: {
        advanceIfCorrectPctAbove: 70,
        reteachIfWrongOptionPctAbove: 40,
      },
      branches,
      generalReteach: [],
    },
  };
}

export function createRevealStep(label = 'New step'): RevealStep {
  return {
    id: uid('step'),
    label,
    elementIds: [],
    annotations: [],
    audio: { source: 'tts', voiceModel: 'james', playbackSpeed: 1 },
  };
}

export function createAnnotation(kind: AnnotationKind, targetElementId: string): Annotation {
  const colors: Record<AnnotationKind, string> = {
    highlight: '#facc15',
    encircle: '#ef4444',
    underline: '#3b82f6',
    annotate: '#6366f1',
    zoom: '#10b981',
    spotlight: '#f59e0b',
  };
  return {
    id: uid('an'),
    kind,
    targetElementId,
    color: colors[kind],
    text: kind === 'annotate' ? 'New note' : undefined,
  };
}

export function createElement(type: ElementType, position?: ElementPosition): ScriptElement {
  return {
    id: uid('el'),
    type,
    content: defaultContent[type],
    style: defaultStyle[type],
    // New text writes itself by default (handwriting on reveal).
    ...(type === 'text' ? { handwritten: true } : {}),
    ...(position ? { position } : {}),
  };
}
